import { ComponentPropsWithoutRef, useState } from "react";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import RadioButton from "components/radio-button";
import { Button } from "react-bootstrap";

import deleteInvoicingSeries from "services/scheduler/deleteInvoicingSeries";
import deleteInvoicingAppointment from "services/scheduler/deleteInvoicingAppointment";

import styles from "./DeleteSeriesModal.module.scss";
import deleteZoomMeeting from "services/zoom/deleteZoomMeeting";

const DELETE_OPTIONS = [
  { label: "Only this session", value: "this_session" },
  {
    label: "This and all future sessions",
    value: "this_and_future_session",
  },
  // {
  //   label: "All sessions in the series",
  //   value: "all_sessions",
  // },
];

type Props = Pick<
  ComponentPropsWithoutRef<typeof ModalLayout>,
  "show" | "onHide" | "onConfirm"
> & {
  appointment: AppointmentWithSeriesInfo & {
    invoice?: InvoiceData;
  };
};

export function extractZoomMeetingId(
  zoomUrl: string | null | undefined,
): string | null {
  if (!zoomUrl) return null;

  const regex = /zoom\.us\/j\/(\d+)/;
  const match = zoomUrl.match(regex);

  return match ? match[1] : null;
}

export function isInvoicePaid(invoice: InvoiceData): boolean {
  return invoice.amount_total_paid >= invoice.amount_total_invoiced;
}

const DeleteSeriesModal = ({ show, onHide, onConfirm, appointment }: Props) => {
  const [deleteOption, setDeleteOption] = useState("this_session");

  const [error, setError] = useState<any>();
  const [busy, setBusy] = useState(false);

  const handleHide = () => {
    setError(undefined);
    setBusy(false);
    setDeleteOption("this_session");
    onHide();
  };

  const onDeleteOptionChange = (event) => {
    const inputField = event.currentTarget;
    setDeleteOption(inputField.value);
  };

  const deleteSeries = async () => {
    setBusy(true);
    try {
      if (deleteOption === "this_session") {
        await deleteInvoicingAppointment(appointment.appointment_id);
      } else if (deleteOption === "all_sessions") {
        await deleteInvoicingSeries(appointment.appointment_series_id, null);
      } else if (deleteOption === "this_and_future_session") {
        await deleteInvoicingSeries(
          appointment.appointment_series_id,
          appointment.appointment_id,
        );
      }
      if (onConfirm) {
        onConfirm();
      }
      handleHide();
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }

    if (deleteOption === "this_session") {
      const { external_meeting_link } = appointment;
      const zoomMeetingId = extractZoomMeetingId(external_meeting_link);

      if (zoomMeetingId) {
        try {
          await deleteZoomMeeting({ meetingId: zoomMeetingId });
        } catch (error) {
          console.error(error);
        }
      }
    }
  };

  return (
    <ModalLayout
      size={"md"}
      className={styles["delete-series-modal"]}
      headerClass={styles["delete-series-modal__header"]}
      bodyClass={styles["delete-series-modal__body"]}
      footerClass={styles["delete-series-modal__footer"]}
      show={show}
      onHide={handleHide}
      error={error}
      Header="Delete Recurring Sessions?"
      Footer={
        <>
          {/* <Button variant="outline-primary" onClick={handleHide}>
            Cancel
          </Button> */}
          <Button onClick={deleteSeries} disabled={busy}>
            {busy ? "Deleting..." : "Confirm"}
          </Button>
        </>
      }
    >
      <p>
        Would you like to delete only this session, OR this and all future
        sessions in the series? Doing so will remove all associated clinical
        notes and payment records.
      </p>
      {DELETE_OPTIONS.map((option) => (
        <RadioButton
          key={option.value}
          className={styles["delete-series-modal__radio"]}
          id={option.value}
          label={option.label}
          value={option.value}
          name="delete-option"
          onChange={onDeleteOptionChange}
          checked={deleteOption === option.value}
        />
      ))}
    </ModalLayout>
  );
};

export default DeleteSeriesModal;
